import { ImgID } from "../../image_loader/img_id.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { CornerSizes, NineSliceDrawer } from "../../image_loader/nine_slicer/nine_slice_drawer.js";
import { UIElement } from "./ui_element.js";
import { ColorScheme } from "./color_scheme.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "./ui_text_element.js";
import { UIImage } from "./ui_image.js";
import { Easing } from "../../transforms/transform_base.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { Vector2 } from "../../common/vector2.js";

// ────────────────────────────────────────────────────────────
// Defaults / constants
// ────────────────────────────────────────────────────────────

const DEFAULT_CORNER_SIZE: number         = 15;
const DEFAULT_FONT_SIZE: number           = 14;
const DEFAULT_VALUE_FONT_SCALE: number    = 0.8;
const DEFAULT_PERCENTAGE_PADDING_Y: number = -1;
const DEFAULT_TEXT_SHADOW_BLUR: number    = 2;
const DEFAULT_TEXT_SHADOW_COLOR: string   = "rgba(0,0,0,0.5)";
const DEFAULT_TEXT_SHADOW_OFFSET: number  = 1;
const DEFAULT_OVERLAY_IMAGE_SIZE: number  = 20;
const DEFAULT_BESIDE_TEXT_W: number       = 40;
const DEFAULT_BESIDE_TEXT_H: number       = 20;
const DEFAULT_CENTER_TEXT_W: number       = 60;
const DEFAULT_CENTER_TEXT_H: number       = 20;
const MIN_CORNER_SUM: number              = 2;
const DEFAULT_ANIM_DURATION_MS: number    = 500;



export interface UIProgressBarOverlayImage
{
  img_id: ImgID;
  position: 'left' | 'right' | 'center';
  offset_x?: number;
  offset_y?: number;
  size?: number;
  width?: number;
  height?: number;
  image_direction_x?: 1 | -1;
  image_direction_y?: 1 | -1;
  text?: string;
  text_position?: 'on_image' | 'beside_image';
  text_offset_x?: number;
  text_offset_y?: number;
  text_font_size?: number;
  text_color?: string;
  attach_to_fill?: boolean;
}


export interface UIProgressBarComponent
{
  img_id: ImgID;
  size?: number;
  offset_y?: number;
}

export interface UIProgressBarComponentsConfig
{
  components: UIProgressBarComponent[];
}

export interface UIProgressBarOptions
{
  base_img_id: ImgID;
  fill_img_id: ImgID;
  base_nine_slice: string;
  fill_nine_slice: string;
  border_width: number;
  initial_progress?: number;
  base_corner_size?: number;
  fill_corner_size?: number;
  corner_size?: number;
  fill_height?: number;
  fill_offset_y?: number;
  show_percentage?: boolean;
  show_value_text?: boolean;
  current_value?: number;
  max_value?: number;
  text_color?: string;
  font_size?: number;
  overlay_images?: UIProgressBarOverlayImage[];
  progress_components?: UIProgressBarComponentsConfig;
  fill_offset_left?: number;
  fill_offset_right?: number;
  shrink_fill_to_zero?: boolean;
  text_vertical_align?: MyTextVerticalAlign;
  text_padding_y?: number;
  value_text_vertical_align?: MyTextVerticalAlign;
  value_text_padding_y?: number;
  percentage_text_vertical_align?: MyTextVerticalAlign;
  percentage_text_padding_y?: number;
  percentage_text_outline_width?: number;
  text_outline_width?: number;
  text_outline_color?: string;
  full_overlay_img_id?: ImgID;
  use_full_overlay?: boolean;
  base_corner_sizes?: CornerSizes;
  fill_corner_sizes?: CornerSizes;
  max_value_text_offset?: { x: number; y: number };
  show_max_value_text?: boolean;
  max_value_text_width?: number;
  max_value_text_color?: string;
}

export class UIProgressBar extends UIElement
{
  private base_img_id: ImgID;
  private fill_img_id: ImgID;
  private base_nine_slice: string;
  private fill_nine_slice: string;
  private border_width: number;

  private value_text_vertical_align: MyTextVerticalAlign;
  private value_text_padding_y: number;

  private percentage_text_vertical_align: MyTextVerticalAlign;
  private percentage_text_padding_y: number;
  private percentage_text_outline_width: number;

  private text_outline_width: number;
  private text_outline_color: string;

  private progress: number;

  private base_corner_size: number;
  private fill_corner_size: number;

  private base_corner_sizes: CornerSizes;
  private fill_corner_sizes: CornerSizes;

  private fill_height?: number;
  private fill_offset_y?: number;

  private max_value_text_offset_x?: number;
  private max_value_text_offset_y?: number;

  private max_value_text_width?: number;
  private max_value_text_color?: string;

  private show_percentage: boolean;
  private text_color: string;
  private font_size: number;

  private max_value_text_element?: UITextElement;
  private percentage_text?: UITextElement;

  private overlay_image_configs?: UIProgressBarOverlayImage[];

  private overlay_image_elements: UIImage[] = [];
  private overlay_text_elements: UITextElement[] = [];

  private show_value_text: boolean;
  private current_value: number;
  private max_value: number;
  private value_text_element?: UITextElement;

  private progress_components_config?: UIProgressBarComponentsConfig;
  private progress_components_elements: UIImage[] = [];

  private fill_offset_left?: number;
  private fill_offset_right?: number;
  private shrink_fill_to_zero: boolean;

  private full_overlay_img_id?: ImgID;
  private use_full_overlay: boolean;
  private full_overlay_element?: UIImage;

  // animation
  private animation_start_progress: number = 0.0;
  private animation_target_progress: number = 0.0;
  private animation_start_time: number = 0.0;
  private animation_duration: number = 0.0;
  private animation_easing: Easing = Easing.EaseOutQuad;

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: UIProgressBarOptions
  )
  {
    super( ctx, x, y, width, height );

    this.base_img_id     = options.base_img_id;
    this.fill_img_id     = options.fill_img_id;
    this.base_nine_slice = options.base_nine_slice;
    this.fill_nine_slice = options.fill_nine_slice;
    this.border_width    = options.border_width;

    if ( options.initial_progress !== undefined )
      this.progress = options.initial_progress;
    else
    if ( options.current_value !== undefined && options.max_value !== undefined )
      this.progress = options.max_value > 0 ? options.current_value / options.max_value : 0.0;
    else
      this.progress = 0.0;

    // кути: нові поля з fallback на старе corner_size або дефолт
    const corner_fallback = options.corner_size ?? DEFAULT_CORNER_SIZE;
    this.base_corner_size = options.base_corner_size ?? corner_fallback;
    this.fill_corner_size = options.fill_corner_size ?? corner_fallback;

    this.base_corner_sizes = options.base_corner_sizes ??
    {
      top_left: { width: this.base_corner_size, height: this.base_corner_size }, bottom_right: { width: this.base_corner_size, height: this.base_corner_size }
    };
    this.fill_corner_sizes = options.fill_corner_sizes ??
    {
      top_left: { width: this.fill_corner_size, height: this.fill_corner_size }, bottom_right: { width: this.fill_corner_size, height: this.fill_corner_size }
    };

    this.fill_height       = options.fill_height;
    this.fill_offset_y     = options.fill_offset_y;
    this.fill_offset_left  = options.fill_offset_left;
    this.fill_offset_right = options.fill_offset_right;
    this.shrink_fill_to_zero = options.shrink_fill_to_zero ?? false;

    this.max_value_text_offset_x = options.max_value_text_offset?.x;
    this.max_value_text_offset_y = options.max_value_text_offset?.y;

    this.max_value_text_width = options.max_value_text_width;
    this.max_value_text_color = options.max_value_text_color;

    this.show_percentage     = options.show_percentage ?? false;
    this.text_color          = options.text_color ?? ColorScheme.white;
    this.font_size           = options.font_size ?? DEFAULT_FONT_SIZE;
    this.overlay_image_configs = options.overlay_images ?? [];

    this.show_value_text = options.show_value_text ?? false;
    this.current_value   = options.current_value ?? 0;
    this.max_value       = options.max_value ?? 1;

    this.progress_components_config = options.progress_components;

    this.full_overlay_img_id = options.full_overlay_img_id;
    this.use_full_overlay = options.use_full_overlay ?? false;

    const common_v_align = options.text_vertical_align ?? MyTextVerticalAlign.Center;
    const common_pad_y   = options.text_padding_y ?? DEFAULT_PERCENTAGE_PADDING_Y;

    this.value_text_vertical_align      = options.value_text_vertical_align      ?? common_v_align;
    this.value_text_padding_y           = options.value_text_padding_y           ?? common_pad_y;

    // за замовчуванням відсоток був знизу — збережемо це як дефолт
    this.percentage_text_vertical_align = options.percentage_text_vertical_align ?? ( options.text_vertical_align ?? MyTextVerticalAlign.Bottom );
    this.percentage_text_padding_y      = options.percentage_text_padding_y      ?? common_pad_y;
    this.percentage_text_outline_width  = options.percentage_text_outline_width  ?? 1;

    this.text_outline_width = options.text_outline_width ?? 1;
    this.text_outline_color = options.text_outline_color ?? ColorScheme.black;

    if ( this.use_full_overlay && this.full_overlay_img_id )
      this.createFullOverlay();

    if ( this.show_value_text )
      this.createValueText();

    if ( options.show_max_value_text )
      this.createMaxValueText();

    if ( this.show_percentage )
      this.createPercentageText();

    if ( this.overlay_image_configs && this.overlay_image_configs.length > 0 )
      this.createOverlayImages();

    if ( this.progress_components_config )
      this.createProgressComponents();
  }

  public setProgress( progress: number ): void
  {
    this.progress = Math.max( 0.0, Math.min( 1.0, progress ) );

    if ( this.show_value_text && this.value_text_element )
    {
      this.current_value = Math.round( this.progress * this.max_value );
      this.value_text_element.setText( `${this.current_value}/${this.max_value}` );
    }

    if ( this.percentage_text )
      this.updatePercentageText();

    if ( this.overlay_image_configs && this.overlay_image_configs.length > 0 )
      for ( let i = 0; i < this.overlay_image_configs.length; i++ )
      {
        const cfg = this.overlay_image_configs[i];
        if ( !cfg.attach_to_fill ) continue;

        const img = this.overlay_image_elements[i];
        const image_w = cfg.width ?? cfg.size ?? DEFAULT_OVERLAY_IMAGE_SIZE;
        const pos = this.calculateOverlayImagePosition( cfg, image_w );

        img.setPixelPos( pos as Vector2, true );

        if ( cfg.text && this.overlay_text_elements[i] )
        {
          const { x, y, width, height } = this.calculateOverlayTextPositionForImage( cfg, image_w, image_w, pos );
          this.overlay_text_elements[i].setText( this.current_value.toString() );
          this.overlay_text_elements[i].setPixelPos( { x, y } as Vector2, true );
        }
      }

    this.setShouldBeRendered();
  }

  public animateToProgress(
    target_progress: number,
    duration: number = DEFAULT_ANIM_DURATION_MS,
    easing: Easing = Easing.EaseOutQuad
  ): void
  {
    const clamped_target = Math.max( 0.0, Math.min( 1.0, target_progress ) );

    this.animation_start_progress = this.progress;
    this.animation_target_progress = clamped_target;
    this.animation_start_time      = performance.now();
    this.animation_duration        = duration;
    this.animation_easing          = easing;

    this.setShouldBeRendered();
  }

  private createValueText(): void
  {
    this.value_text_element = new UITextElement(
      this.ctx,
      0, 0,
      this.rect.getW(),
      this.rect.getH(),
      {
        text:        `${this.current_value}/${this.max_value}`,
        text_style:  TextStyleScheme.rubik,
        text_color:  this.text_color,
        font_size:   this.font_size * DEFAULT_VALUE_FONT_SCALE,
        font_weight: 400,
        text_align:  MyTextAlign.Center,
        vertical_align: this.value_text_vertical_align,
        padding_y:   this.value_text_padding_y,
        shadow_blur: DEFAULT_TEXT_SHADOW_BLUR,
        shadow_color: DEFAULT_TEXT_SHADOW_COLOR,
        shadow_offset_x: DEFAULT_TEXT_SHADOW_OFFSET,
        shadow_offset_y: DEFAULT_TEXT_SHADOW_OFFSET,
        outline_color: ColorScheme.black,
        outline_width: 1
      }
    );

    this.addChild( this.value_text_element );
  }

  private createMaxValueText(): void
  {
    const offset_x = this.max_value_text_offset_x ?? 0;
    const offset_y = this.max_value_text_offset_y ?? 0;

    this.max_value_text_element = new UITextElement(
      this.ctx,
      offset_x, offset_y,
      this.max_value_text_width ?? this.rect.getW(),
      this.rect.getH(),
      {
        text: `${this.max_value}`,
        text_style: TextStyleScheme.rubik,
        text_color: this.max_value_text_color ?? ColorScheme.white,
        font_size: this.font_size,
        font_weight: 700,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false
      }
    );

    this.addChild( this.max_value_text_element );
  }

  private createPercentageText(): void
  {
    this.percentage_text = new UITextElement(
      this.ctx,
      0, 0,
      this.rect.getW(),
      this.rect.getH(),
      {
        text:        this.getPercentageText(),
        text_style:  TextStyleScheme.rubik,
        text_color:  this.text_color,
        font_size:   this.font_size,
        font_weight: 400,
        text_align:  MyTextAlign.Center,
        vertical_align: this.percentage_text_vertical_align,
        padding_y:   this.percentage_text_padding_y,
        shadow_blur: DEFAULT_TEXT_SHADOW_BLUR,
        shadow_color: "rgba( 0, 0, 0, 0.5 )",
        shadow_offset_x: DEFAULT_TEXT_SHADOW_OFFSET,
        shadow_offset_y: DEFAULT_TEXT_SHADOW_OFFSET,
        outline_color: ColorScheme.black,
        outline_width: this.percentage_text_outline_width
      }
    );

    this.addChild( this.percentage_text );
  }

  private updatePercentageText(): void
  {
    if ( !this.percentage_text )
      return;

    this.percentage_text.setText( this.getPercentageText() );
  }

  private getPercentageText(): string
  {
    const percentage = Math.round( this.progress * 100 );
    return `${percentage}%`;
  }

  private createOverlayImages(): void
  {
    if ( !this.overlay_image_configs || this.overlay_image_configs.length === 0 )
      return;

    for ( const config of this.overlay_image_configs )
    {
      const image_w = config.width ?? config.size ?? DEFAULT_OVERLAY_IMAGE_SIZE;
      const image_h = config.height ?? config.size ?? DEFAULT_OVERLAY_IMAGE_SIZE;
      const { x, y }   = this.calculateOverlayImagePosition( config, image_w );

      const img = new UIImage(
        this.ctx,
        x,
        y,
        image_w,
        image_h,
        config.img_id,
        {
          direction_x: config.image_direction_x ?? 1,
          direction_y: config.image_direction_y ?? 1
        }
      );

      this.addChild( img );
      this.overlay_image_elements.push( img );

      if ( config.text )
        this.createOverlayTextForImage( config, image_w, image_h, { x, y } );
    }
  }

  private createOverlayTextForImage(
    config: UIProgressBarOverlayImage,
    image_w: number,
    image_h: number,
    image_pos: { x: number; y: number }
  ): void
  {
    if ( !config.text )
      return;

    const { x, y, width, height } = this.calculateOverlayTextPositionForImage( config, image_w, image_h, image_pos );

    const text_element = new UITextElement(
      this.ctx,
      x,
      y,
      width,
      height,
      {
        text:        config.text,
        text_style:  TextStyleScheme.rubik,
        text_color:  config.text_color ?? ColorScheme.white,
        font_size:   config.text_font_size ?? 12,
        font_weight: 700,
        text_align:  MyTextAlign.Center,
        shadow_blur: DEFAULT_TEXT_SHADOW_BLUR,
        shadow_color: DEFAULT_TEXT_SHADOW_COLOR,
        shadow_offset_x: DEFAULT_TEXT_SHADOW_OFFSET,
        shadow_offset_y: DEFAULT_TEXT_SHADOW_OFFSET,
        outline_color: this.text_outline_color,
        outline_width: this.text_outline_width,
        auto_wrap: false
      }
    );

    this.addChild( text_element );
    this.overlay_text_elements.push( text_element );
  }

  private calculateOverlayImagePosition(
    config: UIProgressBarOverlayImage,
    image_width: number,
  ): { x: number; y: number }
  {
    const base_offset_x = config.offset_x ?? 0;
    const base_offset_y = config.offset_y ?? 0;
    const progress_width = this.rect.getW();
    const progress_height = this.rect.getH();

    let x: number;
    let y: number;

    if ( config.attach_to_fill )
    {
      const pad_left = this.fill_offset_left ?? 0;
      const pad_right = this.fill_offset_right ?? 0;
      const fill_max_width = Math.max( 0, progress_width - this.border_width * 2 - pad_left - pad_right );
      const fill_curr_width = fill_max_width * this.progress;

      const fill_right_x = this.border_width + pad_left + fill_curr_width;

      x = fill_right_x + base_offset_x
    }
    else
    {
      switch ( config.position )
      {
        case 'left':
          x = base_offset_x;
          break;

        case 'right':
          x = progress_width - ( image_width / 2 ) + base_offset_x;
          break;

        case 'center':
        default:
          x = ( progress_width - image_width ) / 2 + base_offset_x;
          break;
      }
    }

    y = progress_height / 2 + base_offset_y;

    return { x, y };
  }

  private calculateOverlayTextPositionForImage(
    config: UIProgressBarOverlayImage,
    image_w: number,
    image_h: number,
    image_pos: { x: number; y: number }
  ): { x: number; y: number; width: number; height: number }
  {
    const base_offset_x = config.text_offset_x ?? 0;
    const base_offset_y = config.text_offset_y ?? 0;
    const text_position = config.text_position ?? 'on_image';

    let x: number;
    let y: number;
    let width: number;
    let height: number;

    if ( text_position === 'on_image' )
    {
      x      = image_pos.x + base_offset_x;
      y      = image_pos.y + base_offset_y;
      width  = image_w;
      height = image_h;
    }
    else
    {
      x      = image_pos.x + image_w + 5 + base_offset_x;
      y      = image_pos.y + ( image_h - DEFAULT_BESIDE_TEXT_H ) / 2 + base_offset_y;
      width  = DEFAULT_BESIDE_TEXT_W;
      height = DEFAULT_BESIDE_TEXT_H;
    }

    return { x, y, width, height };
  }

  public getProgress(): number
  {
    return this.progress;
  }

  public updateOverlayText( index: number, new_text: string ): void
  {
    if ( !this.overlay_image_configs || index < 0 || index >= this.overlay_image_configs.length )
      return;

    const config = this.overlay_image_configs[index];

    if ( !config.text || config.text === new_text )
      return;

    config.text = new_text;

    if ( index < this.overlay_text_elements.length && this.overlay_text_elements[index] )
      this.overlay_text_elements[index].setText( new_text );
  }

  private createProgressComponents(): void
  {
    if ( !this.progress_components_config )
      return;

    if ( this.max_value <= 1 )
      return;

    const config     = this.progress_components_config;
    const fill_width = this.rect.getW() - this.border_width * 2;

    const dividers_count = this.max_value - 1;
    const component_size = config.components[ 0 ]?.size ?? DEFAULT_OVERLAY_IMAGE_SIZE;

    for ( let i = 0; i < dividers_count; i++ )
    {
      const component_config     = config.components[ i % config.components.length ];
      const position_percentage  = ( i + 1 ) / this.max_value;
      const component_x          = fill_width * position_percentage - component_size / 2;
      const component_y          = ( component_config.offset_y ?? 0 ) - component_size / 2;

      const component = new UIImage(
        this.ctx,
        component_x,
        component_y,
        component_size,
        component_size,
        component_config.img_id
      );

      this.progress_components_elements.push( component );
      this.addChild( component );
    }
  }

  private createFullOverlay(): void
  {
    if ( !this.full_overlay_img_id )
      return;

    this.full_overlay_element = new UIImage(
      this.ctx,
      0,
      this.rect.getH()/2,
      this.rect.getW(),
      this.rect.getH(),
      this.full_overlay_img_id
    );

    this.addChild( this.full_overlay_element );
  }

  public updateMaxValue( max_value: number ): void
  {
    if ( max_value < 1 )
      max_value = 1;

    if ( this.max_value === max_value && this.progress_components_elements.length > 0 )
      return;

    // очистка попередніх
    for ( const component of this.progress_components_elements )
      this.removeChild( component );

    this.progress_components_elements = [];
    this.max_value = max_value;

    this.createProgressComponents();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const x = -this.rect.getW2();
    const y = -this.rect.getH2();
    const w =  this.rect.getW();
    const h =  this.rect.getH();

    // ─ Base
    NineSliceDrawer.drawNineSliceImage(
      this.ctx,
      ImagesHolder.getImage( this.base_img_id ),
      this.base_nine_slice,
      x,
      y,
      w,
      h,
      this.base_corner_sizes
    );

    // ─ Fill
    const pad_left  = this.fill_offset_left  ?? 0;
    const pad_right = this.fill_offset_right ?? 0;

    const fill_x           = x + this.border_width + pad_left;
    const fill_max_width   = Math.max( 0, w - this.border_width * 2 - pad_left - pad_right );
    const fill_curr_width  = fill_max_width * this.progress;

    const available_fill_h = h - this.border_width * 2;
    const actual_fill_h    = this.fill_height
      ? Math.min( this.fill_height, available_fill_h )
      : available_fill_h;

    const fill_y = this.fill_offset_y !== undefined
      ? y + this.fill_offset_y
      : y + ( h - actual_fill_h ) / 2;

    if ( this.progress > 0 && fill_curr_width > 0 )
    {
      const fill_image = ImagesHolder.getImage( this.fill_img_id );
      const fill_caps_width = this.fill_corner_sizes.top_left.width
        + this.fill_corner_sizes.bottom_right.width;

      if ( this.shrink_fill_to_zero && fill_curr_width < fill_caps_width )
      {
        // Once the fill is narrower than its two fixed nine-slice caps, draw
        // the complete pill compressed into the remaining width. Keeping the
        // fixed caps here would leave a non-zero capsule until the last frame.
        this.ctx.drawImage(
          fill_image,
          fill_x,
          fill_y,
          fill_curr_width,
          actual_fill_h
        );
      }
      else
      {
        const min_fill_width = this.shrink_fill_to_zero
          ? fill_curr_width
          : Math.max( fill_curr_width, this.fill_corner_size * MIN_CORNER_SUM );
        const actual_fill_w = Math.min( min_fill_width, fill_max_width );

        NineSliceDrawer.drawNineSliceImage(
          this.ctx,
          fill_image,
          this.fill_nine_slice,
          fill_x,
          fill_y,
          actual_fill_w,
          actual_fill_h,
          this.fill_corner_sizes
        );
      }
    }

    this.finishRender();
  }

  protected updateBasic( now: number ): void
  {
    super.updateBasic( now );

    if ( this.animation_duration <= 0 )
      return;

    const elapsed = now - this.animation_start_time;

    if ( elapsed >= this.animation_duration )
    {
      this.progress          = this.animation_target_progress;
      this.animation_duration = 0;

      this.updateProgressTexts();
      this.updateAttachedOverlayPositions();
      this.setShouldBeRendered();
      return;
    }

    const progress_ratio  = elapsed / this.animation_duration;
    const eased_progress  = this.applyEasing( progress_ratio, this.animation_easing );

    this.progress = this.animation_start_progress
                  + ( this.animation_target_progress - this.animation_start_progress ) * eased_progress;

    this.updateProgressTexts();
    this.updateAttachedOverlayPositions();
    this.setShouldBeRendered();
  }

  private updateAttachedOverlayPositions(): void
  {
    if ( !this.overlay_image_configs || this.overlay_image_configs.length === 0 )
      return;

    for ( let i = 0; i < this.overlay_image_configs.length; i++ )
    {
      const cfg = this.overlay_image_configs[i];
      if ( !cfg.attach_to_fill ) continue;

      const img = this.overlay_image_elements[i];
      const image_w = cfg.width ?? cfg.size ?? DEFAULT_OVERLAY_IMAGE_SIZE;
      const pos = this.calculateOverlayImagePosition( cfg, image_w );

      img.setPixelPos( pos as Vector2, true );

      if ( cfg.text && this.overlay_text_elements[i] )
      {
        const { x, y, width, height } = this.calculateOverlayTextPositionForImage( cfg, image_w, image_w, pos );
        this.overlay_text_elements[i].setText( this.current_value.toString() );
        this.overlay_text_elements[i].setPixelPos( { x, y } as Vector2, true );
      }
    }
  }

  private applyEasing( t: number, easing: Easing ): number
  {
    switch ( easing )
    {
      case Easing.EaseInQuad:
        return t * t;

      case Easing.EaseOutQuad:
        return t * ( 2 - t );

      case Easing.EaseInOutQuad:
        return t < 0.5
          ? 2 * t * t
          : -1 + ( 4 - 2 * t ) * t;

      case Easing.EaseInPow:
        return Math.pow( t, 2.0 );

      case Easing.Linear:
      default:
        return t;
    }
  }

  private updateProgressTexts(): void
  {
    this.current_value = Math.round( this.progress * this.max_value );
    if ( this.show_value_text && this.value_text_element )
    {
      this.value_text_element.setText( `${this.current_value}/${this.max_value}` );
    }

    if ( this.percentage_text )
      this.updatePercentageText();
  }

  public name(): string
  {
    return "UIProgressBar";
  }
}

// ────────────────────────────────────────────────────────────
// СЮДИ ДОДАВАТИ НОВІ ПРЕСЕТИ
// ────────────────────────────────────────────────────────────

export class UIProgressBarPresets
{

}
